/* eslint-disable complexity */

import React, { ReactNode, createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { ChartOfAccountsAccount, FinancialConfig } from '@futrli-sdk/predict';
import {
  AvailableLineDataFormulaAssumptions,
  ChartOfAccountBulkFlatItem,
  ConsolidatedForecastListItem,
  FormulaAssumption,
  PreviewAssumptionImpactsResponse
} from '@futrli-sdk/consolidations';
import { useCrudActivityContext, usePromiseState } from '@futrli/shared-services';
import { fetchMergedCOA } from '@futrli/organisations';

import {
  createConsolidatedAssumption,
  readConsolidatedAssumption,
  updateConsolidatedAssumption,
  deleteConsolidatedAssumption,
  readEntityLineData
} from '../../../core/api/consolidated-assumptions';
import {
  AllowedConsolidationAssumptionTypes,
  FullConsolidationAssumptionRequest,
  FullConsolidationAssumptionResponse,
  MultiPaymentTreatment,
  PaymentTreatment,
  PredictionType
} from '../types';
import { AllowedAssumptionTypes } from '../../detail/reports/actions/actions';
import { ASSUMPTION_TYPES, PAYMENT_TYPES } from '../../../core/constants';
import { ConsolidationEntity } from '../../../hooks/useConsolidationEntities';
import { getParsedAccounts, getPredictionType, getPredictionTypeArg } from './utils';
import { PredictManagementContext } from '../../../predict-management/context/predict-management-context';
import { usePredictionBuilderFields } from '../hooks/use-prediction-builder-fields';
import { usePredictionsInfoContext } from '../../detail/context/predictions-info-context';
import { useConsolidationDataContext } from '../../../predict-management/context/consolidation-data-context';
import { useConsolidatedForecastContext } from '../../detail/context/consolidated-forecast-context';
import { useLocalisedConstantsContext } from '../../../../../localisation/src/core/localised-constants-context';
import { useUrlPredictionInfo } from '../hooks/useUrlPredictionInfo/useUrlPredictionInfo';

interface ConsolidatedOrganisationPredictionBuilderContextProps {
  children?: ReactNode;
  urlPredictionId?: string;
  urlPredictionType?: string;
  isQuickCopy?: boolean;
  onClose?: () => void;
}

interface ConsolidatedOrganisationPredictionBuilderContextValues {
  accountsData?: ChartOfAccountsAccount[];
  accountIndex: string;
  currency?: string;
  entity?: ConsolidationEntity;
  financialConfig?: FinancialConfig;
  forecast?: ConsolidatedForecastListItem;
  formulaHasRefs: boolean;
  handleAccountIndexChange: (value: ChartOfAccountsAccount | null) => void;
  handleDeletePrediction: () => Promise<void>;
  handleEntityChange: (value: ConsolidationEntity | null) => void;
  handleAddNewMultiPaymentTreatment: () => void;
  handleChangeMultiPaymentTreatmentData: (index: number, data: MultiPaymentTreatment) => void;
  handleNonCashAccountIndexChange: (value: ChartOfAccountsAccount | null) => void;
  handlePaymentTermsChange: (value: string) => void;
  handlePaymentTreatmentChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handlePredictionNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handlePredictionTypeChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleSaveExisting: (assumptionData: Partial<AllowedConsolidationAssumptionTypes>) => Promise<boolean>;
  handleSaveNew: (
    assumptionData: Partial<AllowedConsolidationAssumptionTypes>
  ) => Promise<FullConsolidationAssumptionResponse | undefined>;
  handleVatPercentageChange: (value: string) => void;
  impactPreviewParams?: Partial<AllowedAssumptionTypes>;
  isCashFlowDisabled: boolean;
  isEditing: boolean;
  isLoading: boolean;
  isQuickCopy: boolean;
  isSaveDisabled: boolean;
  lineData?: AvailableLineDataFormulaAssumptions;
  multiPaymentData: MultiPaymentTreatment[];
  nonCashAccountIndex: string;
  paymentData: FullConsolidationAssumptionRequest['payment_data'];
  paymentTerms: string;
  paymentTreatment: PaymentTreatment;
  paymentType: PAYMENT_TYPES;
  predictionContentTouched: boolean;
  predictionData?: FullConsolidationAssumptionResponse;
  predictionName: string;
  predictionType: PredictionType;
  reloadForecastData: () => void;
  reset: () => void;
  resetUrlData: () => void;
  setAccountIndex: (input: string) => void;
  setAccountsData: (input: ChartOfAccountsAccount[]) => void;
  setFormulaHasRefs: (input: boolean) => void;
  setImpactPreviewParams: (input?: Partial<AllowedAssumptionTypes>) => void;
  setIsSaveDisabled: (disabled: boolean) => void;
  setPredictionContentTouched: (input: boolean) => void;
  setPredictionType: (input: PredictionType) => void;
  setVatPercentage: (input: string) => void;
  urlPredictionId?: string;
  urlPredictionType?: string;
  vatPercentage: string;
  previewImpactsResponse?: PreviewAssumptionImpactsResponse;
  handlePreviewImpactResponseChange: (previewImpactsResponse: PreviewAssumptionImpactsResponse) => void;
}

const context = createContext(({} as unknown) as ConsolidatedOrganisationPredictionBuilderContextValues);

export const ConsolidatedOrganisationPredictionBuilderContext = ({
  children,
  onClose,
  urlPredictionId: urlPredictionIdArg,
  urlPredictionType: urlPredictionTypeArg,
  isQuickCopy: isQuickCopyArg
}: ConsolidatedOrganisationPredictionBuilderContextProps) => {
  const { accountCategoryNames } = useLocalisedConstantsContext();

  const { addErrorMessage } = useContext(PredictManagementContext);
  const { consolidation, currencySymbol, entities, financialConfig } = useConsolidationDataContext();
  const { addReading, removeReading, addUpdating, addCreating, removeCreating } = useCrudActivityContext();
  const { fetchForecastReport, fetchForecast } = usePredictionsInfoContext();
  const { forecast } = useConsolidatedForecastContext();
  const { urlPredictionId, urlPredictionType, resetUrlData } = useUrlPredictionInfo(
    urlPredictionIdArg,
    urlPredictionTypeArg
  );
  const { workspaceId: tempWorkspaceId, organisationId: tempOrganisatioId } = useParams();

  const workspaceId = tempWorkspaceId || '';
  const organisationId = tempOrganisatioId || '';

  const [accountsData, setAccountsData] = useState<ChartOfAccountsAccount[] | undefined>();
  const [entity, setEntity] = useState<ConsolidationEntity>();

  const chartOfAccountsPS = usePromiseState<ChartOfAccountBulkFlatItem[]>(undefined, true);
  const [chartOfAccountsReq, setChartOfAccountsReq, chartOfAccounts] = chartOfAccountsPS;

  const [entityLineDataMap, setEntityLineDataMap] = useState<Record<string, AvailableLineDataFormulaAssumptions>>({});
  const lineDataPS = usePromiseState<AvailableLineDataFormulaAssumptions>(undefined, true);
  const [lineDataReq, setLineDataReq] = lineDataPS;

  const predictionDataPS = usePromiseState<FullConsolidationAssumptionResponse>(undefined, !!urlPredictionId);
  const [predictionDataReq, setPredictionDataReq, predictionData] = predictionDataPS;

  const [formulaHasRefs, setFormulaHasRefs] = useState(false);

  const [previewImpactsResponse, setPreviewImpactResponse] = useState<PreviewAssumptionImpactsResponse>();

  const {
    accountIndex,
    handleAccountIndexChange,
    handleAddNewMultiPaymentTreatment,
    handleChangeMultiPaymentTreatmentData,
    handleNonCashAccountIndexChange,
    handlePaymentTermsChange,
    handlePaymentTreatmentChange,
    handlePredictionNameChange,
    handlePredictionTypeChange,
    handleVatPercentageChange,
    impactPreviewParams,
    isCashFlowDisabled,
    isSaveDisabled,
    multiPaymentData,
    nonCashAccountIndex,
    paymentData,
    paymentTerms,
    paymentTreatment,
    paymentType,
    predictionContentTouched,
    predictionName,
    predictionType,
    reset: resetFields,
    setAccountIndex,
    setImpactPreviewParams,
    setIsSaveDisabled,
    setPredictionContentTouched,
    setPredictionType,
    setVatPercentage,
    vatPercentage
  } = usePredictionBuilderFields(
    predictionData?.assumption_data,
    predictionData?.payment_data,
    predictionData?.payment_type as PAYMENT_TYPES,
    getPredictionType(predictionData?.assumption_type as ASSUMPTION_TYPES)
  );

  const forecastId = forecast?.id;
  const isFirstRun = useRef(true);
  const isInvalid = !predictionName || !entity || !accountIndex || isCashFlowDisabled;
  const lineData = entity ? entityLineDataMap[entity.id] : undefined;
  const onlyShowProfitAndLoss = forecast?.kind === 'budget';

  const isEditing = !isQuickCopyArg && !!urlPredictionId && !!urlPredictionType;

  const isQuickCopy = !!isQuickCopyArg && !!urlPredictionId && !!urlPredictionType;

  // *********
  // * Utils *
  // *********

  const handlePreviewImpactResponseChange = useCallback(
    (newPreviewImpactResponse: PreviewAssumptionImpactsResponse) => {
      setPreviewImpactResponse(newPreviewImpactResponse);
    },
    []
  );

  const loadPredictionData = useCallback(() => {
    if (forecastId && urlPredictionId && urlPredictionType) {
      try {
        return setPredictionDataReq(
          readConsolidatedAssumption(workspaceId, organisationId, forecastId, urlPredictionId)
        );
      } catch (e) {
        addErrorMessage?.(e as Error);
      }
    }
  }, [
    addErrorMessage,
    forecastId,
    organisationId,
    setPredictionDataReq,
    urlPredictionId,
    urlPredictionType,
    workspaceId
  ]);

  const loadLineData = useCallback(async () => {
    if (forecastId && entity && !entityLineDataMap[entity.id]) {
      try {
        setLineDataReq(readEntityLineData(workspaceId, organisationId, forecastId, entity.id));
        const data = await lineDataReq.promise.current;
        if (data) {
          setEntityLineDataMap({
            ...entityLineDataMap,
            [entity.id]: data
          });
        }
      } catch (e) {
        addErrorMessage?.(e as Error);
      }
    }
  }, [
    addErrorMessage,
    entity,
    entityLineDataMap,
    forecastId,
    lineDataReq.promise,
    organisationId,
    setLineDataReq,
    workspaceId
  ]);

  const loadInitialData = useCallback(async () => {
    if (forecastId && consolidation) {
      try {
        addReading('prediction-initial-data');
        const entitytIds = consolidation.organisations.map(o => o.organisation_id);
        const coaReq = setChartOfAccountsReq(fetchMergedCOA(workspaceId, entitytIds));
        const predictionReq = loadPredictionData();
        await Promise.all([coaReq, predictionReq]);
        return true;
      } catch (e) {
        addErrorMessage?.(e as Error);
      } finally {
        removeReading('prediction-initial-data');
      }
    }
    return false;
  }, [
    addErrorMessage,
    addReading,
    consolidation,
    forecastId,
    loadPredictionData,
    removeReading,
    setChartOfAccountsReq,
    workspaceId
  ]);

  const reloadForecastData = useCallback(() => {
    fetchForecastReport!('all');
    fetchForecast!();
  }, [fetchForecast, fetchForecastReport]);

  const reset = useCallback(() => {
    setEntity(undefined);
    resetFields();
  }, [resetFields]);

  // ************
  // * Handlers *
  // ************

  const handleDeletePrediction = useCallback(async () => {
    if (urlPredictionId && forecastId && !isQuickCopy) {
      try {
        await deleteConsolidatedAssumption(workspaceId, organisationId, forecastId, urlPredictionId);

        reloadForecastData();
        onClose?.();
      } catch (e) {
        addErrorMessage?.(e as Error);
      }
    }
  }, [
    urlPredictionId,
    forecastId,
    isQuickCopy,
    workspaceId,
    organisationId,
    reloadForecastData,
    onClose,
    addErrorMessage
  ]);

  const handleEntityChange = useCallback(
    (value: ConsolidationEntity | null) => {
      setEntity(value ? value : undefined);
      setAccountIndex('');
      setVatPercentage('');
    },
    [setAccountIndex, setVatPercentage]
  );

  const handleSaveExisting = useCallback(
    async (assumptionData: Partial<AllowedConsolidationAssumptionTypes>) => {
      if (forecastId && assumptionData && urlPredictionId && entity) {
        const requestData: FullConsolidationAssumptionRequest = {
          organisation_id: entity.id,
          assumption_type: getPredictionTypeArg(predictionType),
          assumption_data: assumptionData,
          payment_type: paymentType as FullConsolidationAssumptionRequest['payment_type'],
          payment_data: paymentData
        };
        try {
          const req = updateConsolidatedAssumption(
            workspaceId,
            organisationId,
            forecastId,
            urlPredictionId,
            requestData
          );
          await addUpdating('prediction', req);
          return true;
        } catch (e) {
          addErrorMessage?.(e as Error);
        }
      }
      return false;
    },
    [
      addErrorMessage,
      addUpdating,
      entity,
      forecastId,
      organisationId,
      paymentData,
      paymentType,
      predictionType,
      urlPredictionId,
      workspaceId
    ]
  );

  const handleSaveNew = useCallback(
    async (assumptionData: Partial<AllowedConsolidationAssumptionTypes>) => {
      if (forecastId && entity) {
        const requestData: FullConsolidationAssumptionRequest = {
          organisation_id: entity.id,
          assumption_type: getPredictionTypeArg(predictionType),
          assumption_data: assumptionData,
          payment_type: paymentType as FullConsolidationAssumptionRequest['payment_type'],
          payment_data: paymentData
        };
        try {
          addCreating('prediction');
          const newAssumption = await createConsolidatedAssumption(
            workspaceId,
            organisationId,
            forecastId,
            requestData
          );
          predictionDataReq.clear();
          loadLineData();
          removeCreating('prediction');
          return newAssumption;
        } catch (e) {
          addErrorMessage?.(e as Error);
        }
      }
    },
    [
      addCreating,
      addErrorMessage,
      entity,
      forecastId,
      loadLineData,
      organisationId,
      paymentData,
      paymentType,
      predictionDataReq,
      predictionType,
      removeCreating,
      workspaceId
    ]
  );

  // ***********
  // * Effects *
  // ***********

  // Load the initial data on first run
  useEffect(() => {
    const load = async () => {
      if (isFirstRun.current) {
        const success = await loadInitialData();
        if (success) {
          isFirstRun.current = false;
        }
      }
    };
    load();
  }, [loadInitialData]);

  // If predictionData changes, update sub-entity field
  useEffect(() => {
    if (entities) {
      if (predictionData) {
        const entity = entities.find(e => e.id === predictionData.organisation_id);
        setEntity(entity);
      } else {
        setEntity(undefined);
      }
    }
  }, [entities, predictionData]);

  // If predictionData changes, update formulaHasRefs
  useEffect(() => {
    if (predictionData && predictionData.assumption_type === ASSUMPTION_TYPES.FORMULA) {
      const data = predictionData.assumption_data as FormulaAssumption;
      setFormulaHasRefs(!!data.formula.match(/REF\(([^)]+)\)/g));
    } else {
      setFormulaHasRefs(false);
    }
  }, [predictionData]);

  // If entity / COA data changes, update our accountsData
  useEffect(() => {
    if (entity && chartOfAccounts) {
      const entityCoa = chartOfAccounts.filter(item => item.organisation_id === entity.id);
      setAccountsData(getParsedAccounts(entityCoa, onlyShowProfitAndLoss, accountCategoryNames));
    }
  }, [chartOfAccounts, entity, onlyShowProfitAndLoss, accountCategoryNames]);

  // If entity changes, set / load available line data for formula references
  useEffect(() => {
    loadLineData();
  }, [loadLineData]);

  const values = {
    accountsData,
    accountIndex,
    currency: currencySymbol,
    entity,
    financialConfig,
    forecast,
    formulaHasRefs,
    handleAccountIndexChange,
    handleDeletePrediction,
    handleEntityChange,
    handleAddNewMultiPaymentTreatment,
    handleChangeMultiPaymentTreatmentData,
    handleNonCashAccountIndexChange,
    handlePaymentTermsChange,
    handlePaymentTreatmentChange,
    handlePredictionNameChange,
    handlePredictionTypeChange,
    handleSaveExisting,
    handleSaveNew,
    handleVatPercentageChange,
    impactPreviewParams,
    isCashFlowDisabled,
    isEditing,
    isLoading: predictionDataReq.busy || chartOfAccountsReq.busy || lineDataReq.busy,
    isQuickCopy,
    isSaveDisabled: isSaveDisabled || isInvalid,
    lineData,
    multiPaymentData,
    nonCashAccountIndex,
    paymentData,
    paymentTerms,
    paymentTreatment,
    paymentType,
    predictionContentTouched,
    predictionData,
    predictionName,
    predictionType,
    reloadForecastData,
    reset,
    resetUrlData,
    setAccountIndex,
    setAccountsData,
    setFormulaHasRefs,
    setImpactPreviewParams,
    setIsSaveDisabled,
    setPredictionContentTouched,
    setPredictionType,
    setVatPercentage,
    urlPredictionId,
    urlPredictionType,
    vatPercentage,
    previewImpactsResponse,
    handlePreviewImpactResponseChange
  };

  return <context.Provider value={values}>{children}</context.Provider>;
};

export const useConsolidatedOrganisationPredictionBuilderContext = () => useContext(context);
